import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Check, CheckCheck, ChevronRight, RefreshCw } from "lucide-react";
import GenericModal from "components/Modal";
import TruncatedWithTooltip from "components/TruncatedWithTooltip";
import {
  NotificationBadgeResponse,
  getNotificationItems,
  getNotificationTotal,
} from "services/Notification/notificationInterface";

/* ────────────────────────────────────────────────────────────────────────
   NotificationBell — header bell with an unread count and a grouped panel.

   Presentational only: it renders whatever the badge summary contains, groups
   it by category (see notificationInterface), and navigates to the owning
   sidebar route when an item is clicked. Fetching/polling lives in
   useNotificationBadge so this stays reusable and testable.
   ──────────────────────────────────────────────────────────────────────── */

/**
 * Small red dot rendered on top of a sidebar tab icon. Wrap the icon in a
 * `relative` element and drop this in as a sibling.
 */
export const NotificationDot: React.FC<{ className?: string }> = ({ className }) => (
  <span
    aria-hidden="true"
    className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800 ${className ?? ""}`}
  />
);

interface NotificationBellProps {
  data: NotificationBadgeResponse | null;
  loading?: boolean;
  onRefresh?: () => void;
  /** Marks every notification as read (PATCH /api/notifications/read-all). */
  onReadAll?: () => Promise<void> | void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ data, loading, onRefresh, onReadAll }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmReadAll, setConfirmReadAll] = useState(false);
  const [readingAll, setReadingAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const total = useMemo(() => getNotificationTotal(data), [data]);
  const items = useMemo(() => getNotificationItems(data), [data]);
  const hasUnread = total > 0;

  const handleConfirmReadAll = async () => {
    if (!onReadAll) return;
    try {
      setReadingAll(true);
      await onReadAll();
      setConfirmReadAll(false);
      setOpen(false);
    } finally {
      setReadingAll(false);
    }
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleNavigate = (sidebarUrl: string, subTab?: string, query?: Record<string, string>) => {
    setOpen(false);
    // Build the destination query from:
    //  • subTab    → `?subtab=` so a page can open the right inner tab
    //    (Profile → "Profile" / Documents → "Documents").
    //  • query     → type-specific params, e.g. ONBOARDING_REVIEW_PENDING carries
    //    { status: "ONBOARDING" } to open Employees filtered to onboarding users.
    const params = new URLSearchParams();
    if (subTab) params.set("subtab", subTab);
    if (query) {
      Object.entries(query).forEach(([key, value]) => params.set(key, value));
    }
    const qs = params.toString();
    navigate(`/admin/${sidebarUrl}${qs ? `?${qs}` : ""}`);
  };

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Notifications"
        onClick={() => setOpen((prev) => !prev)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60"
      >
        {hasUnread ? (
          <BellRing className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Bell className="h-5 w-5" aria-hidden="true" />
        )}
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-white dark:ring-gray-800">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          // z-[1000]: the page's sticky tab bar (Tabs component) is z-50 and, being
          // later in the DOM, would otherwise paint over this panel. Stay below the
          // app modals (GenericModal is z-1301) so dialogs still cover it.
          className="absolute right-0 sm:left-0 sm:right-auto z-[1000] mt-2 w-[92vw] max-w-[360px] origin-top-right sm:origin-top-left overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-gray-800"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</h3>
              {hasUnread && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                  {total} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {onReadAll && hasUnread && (
                <button
                  type="button"
                  onClick={() => setConfirmReadAll(true)}
                  title="Mark all as read"
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  <span>Mark all read</span>
                </button>
              )}
              {onRefresh && (
                <button
                  type="button"
                  onClick={() => onRefresh()}
                  title="Refresh"
                  aria-label="Refresh notifications"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
              )}
            </div>
          </div>

          {/* Body — flat list: title + message, unread dot, hover action */}
          <div className="max-h-[70vh] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="divide-y divide-slate-100 dark:divide-slate-700/70">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="h-8 w-8 shrink-0 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                      <div className="h-2.5 w-1/2 animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-6 py-8 text-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <Check className="h-5 w-5" />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">You're all caught up</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">No new notifications right now.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700/70">
                {items.map((item) => {
                  const Icon = item.meta.icon;
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        /* CHANGE: navigates to item.destination, not
                           item.meta — a type may override its category's
                           route (DOCUMENT_REVIEW_PENDING is a DOCUMENT
                           notification addressed to an admin about someone
                           else's documents, so it opens Employees rather than
                           the viewer's own profile). item.query already
                           carries the subjectId under the name the type
                           declares, e.g. ?employeeId=42. */
                        onClick={() =>
                          handleNavigate(
                            item.destination.sidebarUrl,
                            item.destination.subTab,
                            item.query,
                          )
                        }
                        className="group relative flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40"
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${item.action
                            ? "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
                            : "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"}`}
                        >
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </span>

                        {/* CHANGE: both lines reveal themselves on hover.
                            The message is the one that actually gets cut —
                            it names people and counts ("Documents awaiting
                            verification: Venkates…", "8 reimbursement claims
                            awaiting your appr…") — so the row was hiding the
                            very detail it exists to convey. The title is
                            wrapped too because it is clipped by the same
                            fixed-width row.
                            NOTE: a portal tooltip rather than `title`, because
                            this list is `overflow-y-auto` and an in-flow
                            tooltip would be clipped at that boundary. It
                            appears only when the text is genuinely cut. */}
                        <span className="min-w-0 flex-1">
                          <TruncatedWithTooltip
                            text={item.title}
                            className="text-sm font-semibold text-slate-900 dark:text-white"
                          />
                          <TruncatedWithTooltip
                            text={item.message}
                            wrapperClassName="mt-0.5 block"
                            className="text-xs text-slate-500 dark:text-slate-400"
                          />
                        </span>

                        {/* Unread dot (idle) → swaps to an open affordance on hover */}
                        <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                          <span className="h-2 w-2 rounded-full bg-blue-500 transition-opacity group-hover:opacity-0" />
                          <ChevronRight className="absolute h-4 w-4 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Confirmation before marking everything read (rendered at the root so it
          persists even if the dropdown closes). */}
      {confirmReadAll && (
        <GenericModal
          isOpen={confirmReadAll}
          onClose={() => (readingAll ? undefined : setConfirmReadAll(false))}
          header="Mark all as read"
          customWidth="420px"
        >
          <div className="bg-white dark:bg-gray-800">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This will mark all {total} notification{total === 1 ? "" : "s"} as read. Are you sure?
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmReadAll(false)}
                disabled={readingAll}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReadAll}
                disabled={readingAll}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {readingAll ? "Marking…" : "Yes, mark all read"}
              </button>
            </div>
          </div>
        </GenericModal>
      )}
    </div>
  );
};

export default NotificationBell;
